import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useStore } from '../../stores-v2/StoreContext';
import { ContentTab } from '../../types.ts/general';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import BlogView from './BlogView';
import EmailView from './EmailView';
import MediaView from './MediaView';
import PullQuotes from './PullQuotes';
import SocialClipsView from './SocialClipsView';
import TalkingPointStudio from './TalkingPointStudio';
import TranscriptEditorTab from './TranscriptEditorTab';

import type { FC } from 'react';

const ContentView: FC<{}> = observer(() => {
  const [activeTab, setActiveTab] = useState<ContentTab>(ContentTab.video_clip);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { userIdentityStore, transcriptionStore } = useStore();
  const flags = useFlagsCombination(
    userIdentityStore.currentIdentity?.role as any,
  );
  const [pendingScrollTab, setPendingScrollTab] = useState<ContentTab | null>(
    null,
  );

  // Check if the transcript editor is enabled via LaunchDarkly flag and compatible with current transcript
  const isTranscriptEditorEnabled =
    flags.enableTranscriptEditor &&
    transcriptionStore.isTranscriptEditorCompatible;

  // Check for tab parameter in URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');

    // For transcript editor tab, check if the feature flag is enabled
    // Don't check compatibility immediately as transcription might still be loading
    if (
      tabParam &&
      tabParam === ContentTab.transcript_editor &&
      !flags.enableTranscriptEditor
    ) {
      console.log(
        'Transcript editor tab requested but feature flag is disabled',
      );
      return;
    }

    if (
      tabParam &&
      Object.values(ContentTab).includes(tabParam as ContentTab)
    ) {
      setActiveTab(tabParam as ContentTab);
    }
  }, [searchParams, flags.enableTranscriptEditor]);

  // Separate effect to handle compatibility check after transcription loads
  useEffect(() => {
    // If we're on the transcript editor tab but it's not compatible, redirect
    if (
      activeTab === ContentTab.transcript_editor &&
      flags.enableTranscriptEditor &&
      transcriptionStore.originalTranscription && // Only check after transcription is loaded
      !transcriptionStore.isTranscriptEditorCompatible
    ) {
      console.log(
        'Transcript editor tab is not compatible, redirecting to video_clip',
      );
      setActiveTab(ContentTab.video_clip);
      // Update URL to reflect the change
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', ContentTab.video_clip);
      setSearchParams(newParams);
    }
  }, [
    activeTab,
    flags.enableTranscriptEditor,
    transcriptionStore.originalTranscription,
    transcriptionStore.isTranscriptEditorCompatible,
    searchParams,
    setSearchParams,
  ]);

  // Handle scrolling when transcript data becomes ready
  useEffect(() => {
    if (pendingScrollTab && transcriptionStore.isTranscriptionReady) {
      // Transcript data is now ready, perform the scroll
      setTimeout(() => {
        if (pendingScrollTab === ContentTab.transcript_editor) {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
          });
        }
        setPendingScrollTab(null);
      }, 100);
    }
  }, [pendingScrollTab, transcriptionStore.isTranscriptionReady]);

  // Handle scrolling when page loads directly on transcript tab
  useEffect(() => {
    // If we're on the transcript tab and transcript data becomes ready, scroll to bottom
    if (
      activeTab === ContentTab.transcript_editor &&
      transcriptionStore.isTranscriptionReady
    ) {
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      }, 200);
    }
  }, [activeTab, transcriptionStore.isTranscriptionReady]);

  // Scroll to bottom function for transcript tabs
  const scrollToBottom = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  };

  // Update URL when tab changes
  const handleTabChange = (tab: ContentTab) => {
    // Prevent switching to Transcript Editor if the feature flag is disabled
    if (tab === ContentTab.transcript_editor && !flags.enableTranscriptEditor) {
      return;
    }

    // Handle scrolling for transcript-related tabs
    if (tab === ContentTab.transcript_editor) {
      if (transcriptionStore.isTranscriptionReady) {
        // Transcript data is already loaded, scroll immediately
        scrollToBottom();
      } else {
        // Transcript data is not ready, set pending scroll
        setPendingScrollTab(tab);
      }
    } else {
      // For non-transcript tabs, scroll to the wrapper
      wrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    setActiveTab(tab);

    // Preserve existing parameters while updating the tab
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  return (
    <Main>
      <Tabs>
        {Object.entries(ContentTab).map(([k, v]) => {
          // Skip the Transcript Editor tab if the feature flag is disabled
          if (
            v === ContentTab.transcript_editor &&
            !flags.enableTranscriptEditor
          ) {
            return null;
          }
          return (
            <TabItem
              key={k}
              isSelected={activeTab === v}
              onClick={() => handleTabChange(v)}
            >
              {v}
            </TabItem>
          );
        })}
        {/* {(activeTab === ContentTab.blog || activeTab === ContentTab.email) && (
          <div className="share">
            <ContentShare />
          </div>
        )} */}
      </Tabs>

      {(() => {
        switch (activeTab) {
          case ContentTab.video_clip:
            return (
              <ContentWrapper ref={wrapperRef}>
                <SocialClipsView />
              </ContentWrapper>
            );
          case ContentTab.blog:
            return (
              <ContentWrapper ref={wrapperRef}>
                <BlogView key="blog" type="blog" />
              </ContentWrapper>
            );
          case ContentTab.social_posts:
            return (
              <ContentWrapper ref={wrapperRef}>
                <BlogView key="socialPost" type="socialPost" />
              </ContentWrapper>
            );
          case ContentTab.talking_point_studio:
            return (
              <ContentWrapper ref={wrapperRef}>
                <TalkingPointStudio />
              </ContentWrapper>
            );
          case ContentTab.media:
            return (
              <ContentWrapper ref={wrapperRef}>
                <MediaView />
              </ContentWrapper>
            );
          case ContentTab.pull_quotes:
            return (
              <ContentWrapper ref={wrapperRef}>
                <PullQuotes />
              </ContentWrapper>
            );
          case ContentTab.email:
            return (
              <ContentWrapper ref={wrapperRef}>
                <EmailView />
              </ContentWrapper>
            );
          case ContentTab.transcript_editor:
            // Show the transcript editor if the feature flag is enabled
            // Compatibility will be checked separately and redirect if needed
            if (flags.enableTranscriptEditor) {
              return (
                <ContentWrapper ref={wrapperRef}>
                  <TranscriptEditorTab />
                </ContentWrapper>
              );
            }
            // If flag is disabled, redirect to default tab
            setTimeout(() => handleTabChange(ContentTab.video_clip), 0);
            return null;
          // case ContentTab.external:
          //   return (
          //     <ContentWrapper ref={wrapperRef}>
          //       <ExternalContent />
          //     </ContentWrapper>
          //   );
          default:
            return <ContentWrapper ref={wrapperRef}></ContentWrapper>;
        }
      })()}
    </Main>
  );
});

export default ContentView;

const Main = styled.div`
  margin-top: 10px;
  position: relative;
`;
const Tabs = styled.nav`
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
  gap: 30px;
  align-items: center;
  .copy,
  .share {
    margin-left: auto;
    display: flex;
    gap: 4px;
    align-items: center;
    color: #f2d093;
  }
  margin-bottom: 15px;
  border-bottom: 1px solid #484848;

  @media only screen and (max-width: 750px) {
    width: 100%;
    overflow-x: scroll;
  }
`;
const TabItem = styled.menu<{ isSelected?: boolean }>`
  display: flex;
  padding: 0;
  cursor: pointer;
  color: #f3e9d7;
  &:hover,
  &:active {
    color: #f2d093;
    border-bottom: 1px solid #f2d093;
  }

  ${props =>
    props.isSelected &&
    css`
      color: #f2d093;
      border-bottom: 1px solid #f2d093;
    `}
`;

const ContentWrapper = styled.div`
  min-height: 50vh;
  margin-bottom: 30px;
`;
